import React, { useState,useEffect } from "react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import { useForm } from "react-hook-form";
import { IoBagHandle } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import {
  getCart,
  updateCartItem,
  removeCartItem,
  checkoutCart,
} from "../../api/cartApi";
import { COUNTRY_OPTIONS, US_STATES, toCountryCode, toUsStateCode } from "../../utils/address";
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export const ADDRESS_DRAFT_KEY = "shopperAddressDraft";

const readAddressDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ADDRESS_DRAFT_KEY) || "null");
  } catch {
    return null;
  }
};

const ShopperAdders = () => {
  const [initialDraft] = useState(readAddressDraft);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({ defaultValues: initialDraft?.values || {} });

  // Track checkbox state
  const [sameAsShipping, setSameAsShipping] = useState(!!initialDraft?.sameAsShipping);
  const navigate = useNavigate(); // Navigation hook
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(initialDraft?.selectedAddressId ?? null);

  // Keep the entered address across navigation (e.g. Back from order review) until checkout completes
  useEffect(() => {
    const saveDraft = (values) => {
      try {
        sessionStorage.setItem(
          ADDRESS_DRAFT_KEY,
          JSON.stringify({ values, sameAsShipping, selectedAddressId })
        );
      } catch {}
    };
    saveDraft(getValues());
    const subscription = watch((values) => saveDraft(values));
    return () => subscription.unsubscribe();
  }, [watch, getValues, sameAsShipping, selectedAddressId]);

  const [saveNewAddress, setSaveNewAddress] = useState(true);

  const norm = (v) => String(v || "").trim().toLowerCase();
  // US addresses use the state's code, which the API needs for the tax rate
  const savedRegion = (a) => (toCountryCode(a.country) === "US" ? toUsStateCode(a.region) : a.region);
  // True when the shipping fields are exactly one of the user's saved addresses
  const matchesSavedAddress = () =>
    savedAddresses.some(
      (a) =>
        norm(a.full_name) === norm(getValues("fullName")) &&
        norm(a.phone_number) === norm(getValues("phone")) &&
        norm(a.house_number_street) === norm(getValues("street")) &&
        norm(a.city) === norm(getValues("city")) &&
        norm(savedRegion(a)) === norm(getValues("region")) &&
        norm(a.postal_code) === norm(getValues("postalCode")) &&
        toCountryCode(a.country) === getValues("country")
    );

  // Save a newly typed shipping address to My Address; never blocks checkout if it fails
  const saveAddressIfNew = async (token) => {
    if (!saveNewAddress || matchesSavedAddress()) return;
    const payload = {
      full_name: String(getValues("fullName") || "").trim(),
      email: String(getValues("email") || "").trim(),
      confirmation_email: String(getValues("confirmEmail") || "").trim(),
      phone_number: String(getValues("phone") || "").trim(),
      house_number_street: String(getValues("street") || "").trim(),
      city: String(getValues("city") || "").trim(),
      region: String(getValues("region") || "").trim(),
      postal_code: String(getValues("postalCode") || "").trim(),
      country: getValues("country"),
      is_default: savedAddresses.length === 0,
    };
    // The saved-address API requires a region and a 10+ character phone number
    if (!payload.region || payload.phone_number.length < 10) return;
    try {
      const res = await fetch(`${API_BASE_URL}/buyeraddress`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.result) {
        setSavedAddresses((prev) => [...prev, data.result]);
        setSelectedAddressId(data.result.id);
      }
    } catch (err) {
      console.warn("Could not save address:", err);
    }
  };

  const useNewAddress = () => {
    setSelectedAddressId(null);
    ["fullName", "email", "confirmEmail", "phone", "street", "city", "region", "postalCode"].forEach((field) =>
      setValue(field, "")
    );
  };

  const applySavedAddress = (address) => {
    setSelectedAddressId(address.id);
    setValue("fullName", address.full_name || "");
    setValue("email", address.email || "");
    setValue("confirmEmail", address.confirmation_email || address.email || "");
    setValue("phone", address.phone_number || "");
    setValue("street", address.house_number_street || "");
    setValue("city", address.city || "");
    setValue("country", toCountryCode(address.country));
    setValue("region", savedRegion(address) || "");
    setValue("postalCode", address.postal_code || "");
  };

  // Copy shipping to billing when checked
  const copyShippingToBilling = () => {
    setValue("billingFullName", watch("fullName"));
    setValue("billingEmail", watch("email"));
    setValue("billingConfirmEmail", watch("confirmEmail"));
    setValue("billingPhone", watch("phone"));
    setValue("billingStreet", watch("street"));
    setValue("billingCity", watch("city"));
    setValue("billingRegion", watch("region"));
    setValue("billingPostalCode", watch("postalCode"));
  };

  React.useEffect(() => {
    if (sameAsShipping) {
      copyShippingToBilling();
    }
  }, [
    sameAsShipping,
    watch("fullName"),
    watch("email"),
    watch("confirmEmail"),
    watch("phone"),
    watch("street"),
    watch("city"),
    watch("region"),
    watch("postalCode"),
  ]);

  const isUS = watch("country") === "US";

  const onSubmit = async (data) => {
    console.log(data);
  };
  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const res = await getCart();
        const items = res?.cart_items || res?.cartItems || res?.items || res?.result?.cart_items || [];
        if (mounted) setCartItems(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error("Failed to load cart:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadAddresses() {
      try {
        const token =
          sessionStorage.getItem("accessToken") ||
          localStorage.getItem("accessToken");
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/buyeraddress`, {
          method: "GET",
          headers: { token },
        });
        const data = await res.json();
        const addresses = Array.isArray(data?.result) ? data.result : [];
        if (mounted) {
          setSavedAddresses(addresses);
          const defaultAddress =
            addresses.find((a) => a.is_default) || addresses[0];
          if (defaultAddress && !initialDraft) applySavedAddress(defaultAddress);
        }
      } catch (err) {
        console.error("Failed to load saved addresses:", err);
      }
    }
    loadAddresses();
    return () => (mounted = false);
  }, []);

  function getItemPrice(item) {
    const priceCandidates = [
      item.price,
      item.unit_price,
      item.product_price,
      item.product?.discount_price,
      item.product?.product_price,
      item.product?.discount_price,
    ];
    for (const p of priceCandidates) {
      const n = Number(p);
      if (!Number.isNaN(n)) return n;
    }
    return 0;
  }
  function computeSubtotal() {
    return cartItems.reduce((sum, it) => {
      const qty = Number(it.quantity || it.qty || it.count) || 1;
      return sum + getItemPrice(it) * qty;
    }, 0);
  }
   // Same rule as the API's checkout pricing: one delivery fee per cart item (not per unit), taken from the product
  const deliveryFee = cartItems.reduce(
    (sum, it) => sum + (Number(it.delivery_fee ?? it.product?.delivery_fee) || 0),
    0
  );
  const subtotal = computeSubtotal();
  const total = subtotal + deliveryFee;
  const currencySymbol = cartItems[0]?.seller?.currency_symbol || "$";
  console.log("Subtotal:", subtotal);
  console.log("Total:", total);
  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />

      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />

        <div className="bg-white rounded-2xl p-6 w-full mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm text-[#095228] font-semibold"
            >
              ← BACK
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <form className="space-y-4 " onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4  bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-semibold">Shipping Address</h2>

                {savedAddresses.length > 0 && (
                  <div className="space-y-2">
                    <label className="block font-medium mb-1">
                      Saved Addresses
                    </label>
                    {savedAddresses.map((address) => (
                      <label
                        key={address.id}
                        className="flex items-start gap-2 border rounded-md p-3 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="savedAddress"
                          checked={selectedAddressId === address.id}
                          onChange={() => applySavedAddress(address)}
                        />
                        <span>
                          {address.full_name}, {address.house_number_street},{" "}
                          {address.city}, {address.region},{" "}
                          {address.postal_code}
                          {address.is_default && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                              Default
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                    <label className="flex items-start gap-2 border rounded-md p-3 cursor-pointer">
                      <input
                        type="radio"
                        name="savedAddress"
                        checked={selectedAddressId === null}
                        onChange={useNewAddress}
                      />
                      <span>Use a new address</span>
                    </label>
                  </div>
                )}

                {/* SHIPPING FIELDS */}
                <div>
                  <label className="block font-medium mb-1">
                    Full Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("fullName", {
                      required: "Full Name is required",
                    })}
                    placeholder="Enter your full name"
                    className="w-full border rounded-md p-3 outline-none"
                  />
                  {errors.fullName && (
                    <p className="text-sm text-red-500">
                      {errors.fullName.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      Email Address<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      {...register("email", {
                        required: "Email address is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Enter a valid email address",
                        },
                      })}
                      placeholder="Enter your email address"
                      className="w-full border rounded-md p-3 outline-none"
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      Confirmation Email<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      {...register("confirmEmail", {
                        required: "Confirmation email is required",
                        validate: (value) =>
                          value === watch("email") || "Emails do not match",
                      })}
                      placeholder="Enter your confirmation email"
                      className="w-full border rounded-md p-3 outline-none"
                    />
                    {errors.confirmEmail && (
                      <p className="text-sm text-red-500">
                        {errors.confirmEmail.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Phone Number<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    {...register("phone", {
                      required: "Phone number is required",
                      pattern: {
                        value: /^[\d\s()+.-]{7,}$/,
                        message: "Please provide a valid phone number",
                      },
                    })}
                    placeholder="Enter your phone number"
                    className="w-full border rounded-md p-3 outline-none"
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    House Number and Street Name
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("street", {
                      required: "House number and street name are required",
                    })}
                    placeholder="Enter your house number and street name"
                    className="w-full border rounded-md p-3 outline-none"
                  />
                  {errors.street && (
                    <p className="text-sm text-red-500">
                      {errors.street.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Country<span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register("country", {
                      required: "Country is required",
                      onChange: () => setValue("region", ""),
                    })}
                    className="w-full border rounded-md p-3 outline-none bg-white"
                  >
                    <option value="">Select your country</option>
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className="text-sm text-red-500">
                      {errors.country.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      City<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("city", { required: "City is required" })}
                      placeholder="City"
                      className="w-full border rounded-md p-3 outline-none"
                    />
                    {errors.city && (
                      <p className="text-sm text-red-500">
                        {errors.city.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      {isUS ? "State" : "Region"}
                      <span className="text-red-500">*</span>
                    </label>
                    {isUS ? (
                      <select
                        {...register("region", { required: "State is required" })}
                        className="w-full border rounded-md p-3 outline-none bg-white"
                      >
                        <option value="">Select your state</option>
                        {US_STATES.map(([code, name]) => (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        {...register("region", { required: "Region is required" })}
                        placeholder="Enter your region"
                        className="w-full border rounded-md p-3 outline-none"
                      />
                    )}
                    {errors.region && (
                      <p className="text-sm text-red-500">
                        {errors.region.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Postal Code<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("postalCode", {
                      required: "Postal code is required",
                      pattern: {
                        value: /^[A-Za-z0-9\s-]{3,10}$/,
                        message: "Please provide a valid postal code",
                      },
                    })}
                    placeholder="Enter your postal code"
                    className="w-full border rounded-md p-3 outline-none"
                  />
                  {errors.postalCode && (
                    <p className="text-sm text-red-500">
                      {errors.postalCode.message}
                    </p>
                  )}
                </div>

                {!matchesSavedAddress() && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveNewAddress}
                      onChange={(e) => setSaveNewAddress(e.target.checked)}
                      className="accent-[#095228]"
                    />
                    Save this address to My Address for future orders
                  </label>
                )}
              </div>

              {/* BILLING FIELDS */}
              <div className="space-y-4  bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-semibold">Billing Address</h2>
                <div className="flex items-center mb-4">
                  <input
                    id="sameAsShipping"
                    type="checkbox"
                    checked={sameAsShipping}
                    onChange={() => setSameAsShipping((prev) => !prev)}
                    className="form-checkbox h-5 w-5 text-green-600"
                  />
                  <label htmlFor="sameAsShipping" className="ml-2 text-base">
                    Same as my shipping address
                  </label>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Full Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("billingFullName", {
                      required: "Full Name is required",
                    })}
                    placeholder="Enter your full name"
                    className="w-full border rounded-md p-3 outline-none"
                    disabled={sameAsShipping}
                  />
                  {errors.billingFullName && (
                    <p className="text-sm text-red-500">
                      {errors.billingFullName.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      Email Address<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      {...register("billingEmail", {
                        required: "Email address is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Enter a valid email address",
                        },
                      })}
                      placeholder="Enter your email address"
                      className="w-full border rounded-md p-3 outline-none"
                      disabled={sameAsShipping}
                    />
                    {errors.billingEmail && (
                      <p className="text-sm text-red-500">
                        {errors.billingEmail.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      Confirmation Email<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      {...register("billingConfirmEmail", {
                        required: "Confirmation email is required",
                        validate: (value) =>
                          value === watch("billingEmail") ||
                          "Emails do not match",
                      })}
                      placeholder="Enter your confirmation email"
                      className="w-full border rounded-md p-3 outline-none"
                      disabled={sameAsShipping}
                    />
                    {errors.billingConfirmEmail && (
                      <p className="text-sm text-red-500">
                        {errors.billingConfirmEmail.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Phone Number<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    {...register("billingPhone", {
                      required: "Phone number is required",
                      pattern: {
                        value: /^[\d\s()+.-]{7,}$/,
                        message: "Please provide a valid phone number",
                      },
                    })}
                    placeholder="Enter your phone number"
                    className="w-full border rounded-md p-3 outline-none"
                    disabled={sameAsShipping}
                  />
                  {errors.billingPhone && (
                    <p className="text-sm text-red-500">
                      {errors.billingPhone.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    House Number and Street Name
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("billingStreet", {
                      required: "House number and street name are required",
                    })}
                    placeholder="Enter your house number and street name"
                    className="w-full border rounded-md p-3 outline-none"
                    disabled={sameAsShipping}
                  />
                  {errors.billingStreet && (
                    <p className="text-sm text-red-500">
                      {errors.billingStreet.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full">
                    <label className="block font-medium mb-1">
                      City<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("billingCity", {
                        required: "City is required",
                      })}
                      placeholder="City"
                      className="w-full border rounded-md p-3 outline-none"
                      disabled={sameAsShipping}
                    />
                    {errors.billingCity && (
                      <p className="text-sm text-red-500">
                        {errors.billingCity.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <label className="block font-medium mb-1">Region</label>
                    <input
                      type="text"
                      {...register("billingRegion")}
                      placeholder="Enter your region"
                      className="w-full border rounded-md p-3 outline-none"
                      disabled={sameAsShipping}
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    Postal Code<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("billingPostalCode", {
                      required: "Postal code is required",
                      pattern: {
                        value: /^[A-Za-z0-9\s-]{3,10}$/,
                        message: "Please provide a valid postal code",
                      },
                    })}
                    placeholder="Enter your postal code"
                    className="w-full border rounded-md p-3 outline-none"
                    disabled={sameAsShipping}
                  />
                  {errors.billingPostalCode && (
                    <p className="text-sm text-red-500">
                      {errors.billingPostalCode.message}
                    </p>
                  )}
                </div>
              </div>
              {/* <button
                type="submit"
                className="mt-4 w-full bg-green-700 text-white font-semibold py-3 rounded-md"
              >
                Submit
              </button> */}
            </form>
          </div>

          {/* Right: Order Summary */}
          <div className="border rounded-2xl p-6 bg-white shadow-sm h-fit">
            <h3 className="text-xl font-semibold mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                    <span>{currencySymbol}{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery Fee</span>
                    <span>{currencySymbol}{deliveryFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Tax</span>
                <span>Calculated at next step</span>
              </div>
              <hr />
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                    <span>{currencySymbol}{total.toFixed(2)}</span>
              </div>
            </div>
           <button
  onClick={async () => {
    try {
      const token =
        sessionStorage.getItem("accessToken") ||
        localStorage.getItem("accessToken");

      if (!token) {
        alert("Please log in before proceeding to checkout.");
        return;
      }

      const isShippingValid = await trigger([
        "fullName",
        "email",
        "confirmEmail",
        "phone",
        "street",
        "country",
        "city",
        "region",
        "postalCode",
      ]);
      if (!isShippingValid) return;

      await saveAddressIfNew(token);

      const body = {
        payment_method: "card",
        // payment_method_id: "string",
        shipping_address: {
          name: watch("fullName") || "Guest",
          email:watch("email") || "",
          address_line_1: watch("street") || "",
          address_line_2: "",
          city: watch("city") || "",
          state: watch("region") || "",
          postal_code: watch("postalCode") || "",
          country: watch("country"),
          phone: watch("phone") || "",
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/cart/checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();
      console.log("Checkout Response:", data);
if (data?.result) {
  // save minimal data as JSON strings
  sessionStorage.setItem("checkoutResponse", JSON.stringify(data.result));
  sessionStorage.setItem("shippingAddress", JSON.stringify(body.shipping_address));
}
      if (response.ok && data.success) {
        // ✅ Navigate after successful checkout
navigate("/ordereview", {
  state: {
    checkoutResponse: data.result,
    shippingAddress: body.shipping_address,
  },
});
      } else {
        alert(data?.message || "Checkout failed. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      alert("Something went wrong while processing checkout.");
    }
  }}
  className="mt-6 w-full bg-[#095228] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
>
  <IoBagHandle className="text-xl" />
  Continue to Payment
</button>

            {/* <p className="text-xs text-red-500 text-center mt-3">
              *Please select color and size
            </p> */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ShopperAdders;
